import { useContext, useState, useEffect } from "react";
import {
  Table,
  Tag,
  message,
  Row,
  Col,
  Card,
  Spin,
  DatePicker,
  Modal,
  Select,
  Descriptions,
  Button,
  Space,
  Input,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  getUpdateAttandance,
  getallDeveloperForTask,
  updateRequest,
} from "../../utils/API";
import { AuthContext } from "../../Auth/AuthContext";
import {
  AiOutlineCheckCircle,
  AiOutlineClockCircle,
  AiOutlineCloseCircle,
} from "react-icons/ai";
import { FaSyncAlt } from "react-icons/fa";

const { MonthPicker } = DatePicker;
const { Option } = Select;

interface MissPunchRecord {
  date: string;
  _id: string;
  attendanceId: {
    _id: string;
    userId: {
      _id: string;
      username: string;
    };
    date: string;
    clockIn: string;
    lunchStart: string;
    lunchEnd: string;
    clockOut: string;
    status: any;
    endTime: string;
    reason: string; // Ensure this is included
  };
  updatedData: {
    [x: string]: string | undefined;
    clockIn?: string;
    clockOut?: string;
    lunchStart?: string;
    lunchEnd?: string;
    status?: any;
    endTime?: string;
  };
  status: string;
  reason?: string; // Top-level reason
  adminResponse?: string;
  photo?: string;
}

const MissPunchTable = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { authData } = useContext(AuthContext);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MissPunchRecord[]>([]);
  const [filteredData, setFilteredData] = useState<MissPunchRecord[]>([]);
  const [developers, setDevelopers] = useState<any[]>([]);
  const [selectedDeveloper, setSelectedDeveloper] = useState<string | null>(
    null,
  );
  const [selectedRecord, setSelectedRecord] = useState<MissPunchRecord | null>(
    null,
  );
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [selectedMonthNum, setSelectedMonthNum] = useState<number>(
    dayjs().month() + 1,
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const statusStyles: any = {
    Pending: { background: "#fff3cd", color: "#856404" }, // Warning
    Approved: { background: "#d4edda", color: "#155724" }, // Success
    Rejected: { background: "#f8d7da", color: "#721c24" }, // Danger
  };

  useEffect(() => {
    fetchMissPunchData();
    if (authData?.roleId?.roleId === 1) {
      fetchDevelopers();
    }
  }, [
    authData,
    selectedDeveloper,
    selectedMonthNum,
    selectedYear,
    page,
    selectedStatus,
  ]);

  useEffect(() => {
    if (selectedStatus) {
      const filtered = data.filter(
        (record) => record.status === selectedStatus,
      );
      setFilteredData(filtered);
      setTotalPages(filtered.length);
    } else {
      setFilteredData(data);
      setTotalPages(data.length);
    }
  }, [selectedStatus, data]);

  const fetchDevelopers = async () => {
    try {
      const result = await getallDeveloperForTask("", "");
      setDevelopers(result.data);
    } catch (error) {
      message.error("Failed to fetch developers.");
    }
  };

  const fetchMissPunchData = async () => {
    try {
      setLoading(true);
      const response = await getUpdateAttandance({
        userId:
          authData?.roleId?.roleId === 1
            ? selectedDeveloper || null
            : authData?.user,
        month: selectedMonthNum,
        year: selectedYear,
        status: selectedStatus,
        page: page,
        limit: 10,
      });

      if (!response?.data) {
        setData([]);
        setFilteredData([]);
        setTotalRecords(0);
        setTotalPages(0);
        return;
      }

      const safeData = Array.isArray(response.data) ? response.data : [];
      setData(safeData);
      setFilteredData(safeData);
      setTotalRecords(response.total || 0); // Set total records
      setTotalPages(response.totalPages || 0);
    } catch (error) {
      message.error("Failed to fetch miss punch records.");
      setData([]);
      setFilteredData([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (status: "Approved" | "Rejected") => {
    if (!selectedRecord) return;

    try {
      setActionLoading(status.toLowerCase());
      await updateRequest(selectedRecord._id, {
        status,
        reason: selectedRecord.adminResponse || "",
      });

      // Refresh data after update
      await fetchMissPunchData();

      message.success(`Request ${status.toLowerCase()} successfully.`);
      setIsModalOpen(false);
    } catch (error) {
      message.error(`Failed to ${status.toLowerCase()} request.`);
    } finally {
      setActionLoading(null);
    }
  };

  const columns: ColumnsType<MissPunchRecord> = [
    {
      title: "#",
      key: "index",
      render: (_, __, index) => (page - 1) * 10 + index + 1,
    },
    ...(authData?.roleId?.roleId === 1
      ? [
          {
            title: "Developer Name",
            key: "username",
            render: (
              _: any,
              record: { attendanceId: { userId: { username: any } } },
            ) => record.attendanceId.userId.username || "-",
          },
        ]
      : []),
    {
      title: "Date",
      key: "date",
      render: (_, record) =>
        dayjs(record.attendanceId.date).format("DD-MM-YYYY"),
    },
    {
      title: "Clock In",
      key: "actualClockIn",
      render: (_, record) => record.attendanceId.clockIn || "-",
    },
    {
      title: "Lunch Start",
      key: "actualLunchStart",
      render: (_, record) => record.attendanceId.lunchStart || "-",
    },
    {
      title: "Lunch Complete",
      key: "actualLunchComplete",
      render: (_, record) => record.attendanceId.lunchEnd || "-",
    },
    {
      title: "Clock Out",
      key: "actualClockOut",
      render: (_, record) => record.attendanceId.clockOut || "-",
    },
    {
      title: "Total Time",
      dataIndex: "endTime",
      key: "endTime",
      render: (_, record) => record.attendanceId.endTime || "-",
    },
    {
      title: "Remarks",
      dataIndex: "reason",
      key: "reason",
      width: 180,
      render: (text: string, record: MissPunchRecord) => {
        const displayText = text || record.attendanceId?.reason || "-";
        const shortPreview =
          displayText.length > 40
            ? `${displayText.substring(0, 40)}...`
            : displayText;

        const content = (
          <Typography.Paragraph
            ellipsis={{ rows: 2, expandable: false }}
            className="!mb-0 !text-sm max-w-[200px]"
          >
            {shortPreview}
          </Typography.Paragraph>
        );

        if (displayText.length > 40) {
          return (
            <Tooltip
              title={
                <div className="max-w-xs whitespace-pre-line text-sm">
                  {displayText}
                </div>
              }
              placement="topLeft"
              overlayInnerStyle={{
                whiteSpace: "pre-line",
                color: "#000",
                padding: "12px",
              }}
              color="#CCCCCC"
            >
              <div className="flex items-start gap-2 text-gray-700 hover:text-black cursor-pointer">
                {content}
              </div>
            </Tooltip>
          );
        }

        return (
          <div className="flex items-start gap-2 text-gray-700 hover:text-black">
            {content}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const style = statusStyles[record.status] || {
          background: "#e2e3e5",
          color: "#383d41",
        };

        return (
          <span
            className="badge rounded-pill"
            style={{
              backgroundColor: style.background,
              color: style.color,
            }}
          >
            {record.status}
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Tooltip title="View Updated Data">
          <span
            onClick={() => {
              setSelectedRecord(record);
              setIsModalOpen(true);
            }}
            className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-400 to-emerald-500 
                   shadow-[0_4px_12px_rgba(34,211,238,0.3)] 
                   hover:shadow-[0_6px_16px_rgba(34,211,238,0.4)] 
                   active:translate-y-1 active:shadow-none 
                   text-white flex items-center justify-center 
                   transition-all duration-200 group cursor-pointer"
          >
            <FaSyncAlt
              className="group-hover:scale-110 transition-transform"
              size={12}
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        width={900}
        style={{ top: 40 }}
        styles={{
          header: {
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "#f1f5ffff",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          body: {
            padding: "0.25px",
            fontFamily: "inter, sans-serif",
          },
        }}
        title={
          <div
            style={{
              color: "#4b5563",
              fontFamily: "inter, sans-serif",
              fontSize: "20px",
              fontWeight: 500,
            }}
          >
            Miss Punch Request Details
          </div>
        }
        footer={
          selectedRecord?.status === "Pending" ||
          selectedRecord?.status === "Rejected" ? (
            <Row justify="end" style={{ padding: "0px 15px 10px 1px" }}>
              <Space>
                <Button
                  danger
                  onClick={() => handleAction("Rejected")}
                  loading={actionLoading === "rejected"}
                >
                  Reject
                </Button>
                <Button
                  type="primary"
                  style={{ background: "#001529", borderColor: "#001529" }}
                  onClick={() => handleAction("Approved")}
                  loading={actionLoading === "approved"}
                >
                  Approve
                </Button>
              </Space>
            </Row>
          ) : selectedRecord?.status === "Approved" ? (
            <Row justify="end" style={{ padding: "0px 15px 10px 1px" }}>
              <Tag
                color={
                  statusStyles[selectedRecord.status]?.background || "#d9d9d9"
                }
                style={{
                  color: statusStyles[selectedRecord.status]?.text || "#000",
                }}
              >
                {selectedRecord.status}
              </Tag>
            </Row>
          ) : null
        }
      >
        <div className="elements">
          {selectedRecord && (
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <Table
                dataSource={[selectedRecord]}
                columns={[
                  {
                    title: "Developer Name",
                    dataIndex: ["attendanceId", "userId", "username"],
                    render: (text) => text || "-",
                    width: 150,
                  },
                  {
                    title: "Date",
                    render: () =>
                      dayjs(selectedRecord.attendanceId.date).format(
                        "DD-MM-YYYY",
                      ) || "-",
                    width: 120,
                  },
                  {
                    title: "Clock In",
                    render: () => selectedRecord.updatedData.clockIn || "-",
                    width: 120,
                  },
                  {
                    title: "Lunch Start",
                    render: () => selectedRecord.updatedData.lunchStart || "-",
                    width: 120,
                  },
                  {
                    title: "Lunch End",
                    render: () => selectedRecord.updatedData.lunchEnd || "-",
                    width: 120,
                  },
                  {
                    title: "Clock Out",
                    render: () => selectedRecord.updatedData.clockOut || "-",
                    width: 120,
                  },
                  {
                    title: "Total Time",
                    render: () => selectedRecord.updatedData.endTime || "-",
                    width: 120,
                  },
                  {
                    title: "Status",
                    key: "status",
                    render: (_, record) => {
                      const style = statusStyles[record.status] || {
                        background: "#e2e3e5",
                        color: "#383d41",
                      };
                      return (
                        <span
                          className="badge rounded-pill"
                          style={{
                            backgroundColor: style.background,
                            color: style.color,
                          }}
                        >
                          {record.status}
                        </span>
                      );
                    },
                  },
                ]}
                pagination={false}
                scroll={{ x: true }}
                bordered
                size="small"
              />
              <div style={{ marginTop: 20 }}>
                <label style={{ fontWeight: 600 }}>Photo</label>

                {selectedRecord?.photo ? (
                  <div
                    style={{
                      position: "relative",
                      marginTop: 10,
                      width: "100%",
                      maxWidth: 850,
                      height: 250,
                      overflow: "hidden",
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                  >
                    <a
                      href={`${process.env.REACT_APP_IMAGE_URL}/miss-match/${selectedRecord.photo}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <img
                        src={`${process.env.REACT_APP_IMAGE_URL}/miss-match/${selectedRecord.photo}`}
                        alt="Miss Punch Photo"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          border: "3px solid #ccc",
                          borderRadius: 8,
                          display: "block",
                        }}
                      />

                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          backgroundColor: "rgba(0,0,0,0.5)",
                          color: "#fff",
                          padding: "4px 12px",
                          borderRadius: 6,
                          fontSize: 16,
                          fontWeight: 500,
                          pointerEvents: "none",
                        }}
                      >
                        👆 Preview
                      </div>
                    </a>
                  </div>
                ) : (
                  <div
                    style={{ marginTop: 8, fontStyle: "italic", color: "#888" }}
                  >
                    No photo provided
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16 }}>
                <label style={{ fontWeight: 600 }}>Remark</label>
                <Input.TextArea
                  rows={3}
                  placeholder="Enter your remark (optional)"
                  style={{ marginTop: 8 }}
                  onChange={(e) =>
                    setSelectedRecord((prev) =>
                      prev ? { ...prev, adminResponse: e.target.value } : null,
                    )
                  }
                  value={selectedRecord.adminResponse || ""}
                />
              </div>
            </div>
          )}
        </div>
        <div className="footer-modals " />
      </Modal>

      <div style={{ marginTop: "1rem", padding: "0 16px" }}>
        <Row
          gutter={[16, 16]}
          align="middle"
          justify="space-between"
          style={{
            marginBottom: "1rem",
            width: "100%",
          }}
        >
          {/* Title Column */}
          <Col
            xs={24}
            sm={1}
            md={6}
            lg={4}
            xl={3}
            style={{
              minWidth: 150,
              paddingRight: "16px",
            }}
          >
            <h2 className="headername">MissPunch</h2>
          </Col>

          {/* Filters Row - Developer, Status, Month/Year */}
          <Col
            xs={24}
            sm={18}
            md={18}
            lg={18}
            xl={19}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            {/* Developer Select - Only for admin */}
            {authData?.roleId?.roleId === 1 && (
              <Select
                showSearch
                style={{ width: 180, minWidth: 180 }}
                placeholder="Select developer"
                value={selectedDeveloper}
                onChange={(value: string) => {
                  setSelectedDeveloper(value || null);
                  setPage(1);
                }}
                allowClear
                onClear={() => {
                  setSelectedDeveloper(null);
                  setPage(1);
                }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children?.toString()?.toLowerCase() || "").includes(
                    input.toLowerCase(),
                  )
                }
              >
                {developers.map((dev) => (
                  <Select.Option key={dev._id} value={dev._id}>
                    {dev.username}
                  </Select.Option>
                ))}
              </Select>
            )}

            {/* Status Select */}
            <Select
              style={{ width: 150, minWidth: 150 }}
              placeholder="Select status"
              value={selectedStatus}
              onChange={(value) => {
                setSelectedStatus(value || null);
                setPage(1);
              }}
              allowClear
              options={[
                { value: "Approved", label: "Approved" },
                { value: "Pending", label: "Pending" },
                { value: "Rejected", label: "Rejected" },
              ]}
            />

            {/* Month/Year Selectors */}
            <Select
              style={{
                width: 120,
                minWidth: 120,
              }}
              value={selectedMonthNum}
              allowClear
              onChange={(value) => {
                setSelectedMonthNum(value);
                setPage(1);
              }}
              options={[
                { value: 1, label: "January" },
                { value: 2, label: "February" },
                { value: 3, label: "March" },
                { value: 4, label: "April" },
                { value: 5, label: "May" },
                { value: 6, label: "June" },
                { value: 7, label: "July" },
                { value: 8, label: "August" },
                { value: 9, label: "September" },
                { value: 10, label: "October" },
                { value: 11, label: "November" },
                { value: 12, label: "December" },
              ]}
            />

            <Select
              style={{
                width: 100,
                minWidth: 100,
              }}
              value={selectedYear}
              allowClear
              onChange={(value) => {
                setSelectedYear(value);
                setPage(1);
              }}
              options={Array.from({ length: 20 }, (_, i) => {
                const year = dayjs().year() - 10 + i;
                return { value: year, label: year.toString() };
              })}
            />
          </Col>
        </Row>
      </div>

      <Card className="">
        <Spin spinning={loading}>
          <div
            className="scrollbar-hide custom-scroll-table"
            style={{ overflowX: "auto" }}
          >
            <Table
              columns={columns}
              dataSource={filteredData}
              loading={loading}
              scroll={{ x: "max-content" }}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                current: page,
                onChange: (pageNumber) => setPage(pageNumber),
                showSizeChanger: false,
                total: totalRecords,
              }}
            />
          </div>
        </Spin>
      </Card>
    </>
  );
};

export const REQUEST_ATTENDANCE_TABLE_COLUMNS = [
  "attendanceId",
  "status",
  "reason",
];

export default MissPunchTable;
